import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi } from '../api'
import { backendScopeFor, type FleetSegment } from '../models/fleetSegments'
import type { FlagCount } from '../models/flagFilter'

/** Задержка перед запросом судов, мс. Клик тумблера / смена вида / оргкабинета /
 *  принудительный рефреш (сигнатура входов изменилась) → рисуем СРАЗУ (0). Изменился
 *  только вьюпорт (пан/зум — сигнатура та же) → дебаунсим 250 мс, чтобы пан-шторм
 *  moveend не превращался в шторм запросов. Первый прогон (prev undefined) → сразу. */
export function fleetFetchDelayMs(prevInputsSig: string | undefined, inputsSig: string): number {
  return prevInputsSig !== undefined && prevInputsSig === inputsSig ? 250 : 0
}

/** Что делать со слоем флота на этом прогоне эффекта. */
export type FleetLayerAction = 'skip' | 'clear' | 'fetch'

/** Решение отдельно от эффекта: тестов React-хуков в проекте нет, а правило
 *  «во время записи не вмешиваемся» стоит дороже своей строчки.
 *
 *  `skip` во время записи важнее, чем кажется, и он ГЛАВНЕЕ пустого набора
 *  сегментов. Снять слой на середине ролика — значит убрать из кадра весь флот;
 *  позиции в это время ставит запись (`map/fleetHistory.ts`), и слой обязан
 *  оставаться ровно таким, каким его оставил предыдущий кадр. */
export function fleetLayerAction(
  paused: boolean | undefined,
  scopeCount: number,
): FleetLayerAction {
  if (paused) return 'skip'
  return scopeCount === 0 ? 'clear' : 'fetch'
}

// Начертание маркера судна (форма по знанию курса, цвет по свежести позиции)
// живёт в vesselMarkerStyle.ts — там же его тесты.

/** Ежечасное обновление живых позиций (мс): страница может висеть открытой. */
const VESSELS_REFRESH_MS = 3_600_000

interface FleetLayerOptions {
  fleet: FleetSegment[]
  species: number | null
  showUncategorized: boolean
  /** Выбор флагов «Ин.флота»; пусто = все флаги. */
  flags: string[]
  /** Куда сложить флаги текущего кадра со счётчиками (их отдаёт scope=foreign).
   *  Из них строится сам список выбора — зашитый набор врал бы: флаги в
   *  Беринговом море и в Японском разные. */
  onFlagCounts?: (counts: FlagCount[]) => void
  /** Ключ вьюпорта (центр+зум): пан/зум → перезапрос по видимому bbox. */
  viewKey: string
  /** Активная организация: scope=own зависит от кабинета. */
  orgId: unknown
  /** Счётчик принудительного перезапроса. Меняется, когда фоновое обновление
   *  позиций из MarineTraffic дописало точки в базу: вьюпорт при этом тот же,
   *  и без отдельного ключа слой не узнал бы, что данные изменились. */
  refreshKey?: number
  /** Идёт запись ролика — слой не трогаем.
   *
   *  На время записи позиции судов ставит кадр (`map/fleetHistory.ts`): они едут
   *  по историческим трекам вместе с датой на таймере. Ежечасное обновление или
   *  ответ запроса, начатого до записи, затёр бы кадровые позиции живыми — и
   *  посреди ролика весь флот разом прыгнул бы в «сегодня». Тот же приём, что
   *  глушит свободный такт комет (`recordingRef` в ModelsPage). */
  paused?: boolean
  /** Свежий набор судов кадра — тем, кто держит СНИМОК свойства судна.
   *
   *  Единственный такой держатель — закреплённая карточка судна: она снята в миг
   *  клика и сама о переезде судна не узнаёт (см. `models/cardPick.refreshedPick`).
   *  Колбэк держим в ref по той же причине, что и `onFlagCounts`: в зависимостях
   *  эффекта он перезапускал бы запрос судов на каждый рендер страницы. */
  onVessels?: (features: GeoJSON.Feature[]) => void
}

/**
 * Слой живых AIS-судов. Общий хук для «Карт» и «Аналитики»: флот рисуется на карте
 * обоих разделов из одного стора (ViewContext). Перенос эффекта из ModelsPage без
 * изменения поведения: три независимых сегмента (свой/РФ/ин.флот), дедуп по mmsi
 * (копия own приоритетна — в ней юр.лицо), дебаунс входного всплеска + ежечасное
 * обновление, все запросы упали → чужого под своей подписью не рисуем.
 */
export function useFleetLayer(
  mapRef: RefObject<MapCanvasHandle | null>,
  {
    fleet, species, showUncategorized, flags, onFlagCounts, viewKey, orgId, refreshKey,
    paused, onVessels,
  }: FleetLayerOptions,
) {
  // Сигнатура «содержательных» входов (всё, КРОМЕ вьюпорта). Клик по ней меняет её и
  // рисует сразу; смена только viewKey оставляет её прежней → дебаунс (см. ниже).
  const prevSigRef = useRef<string>()
  // Колбэк держим в ref: он пересоздаётся на каждый рендер страницы, и в
  // зависимостях эффекта перезапускал бы запрос судов без причины.
  const onFlagCountsRef = useRef(onFlagCounts)
  onFlagCountsRef.current = onFlagCounts
  const onVesselsRef = useRef(onVessels)
  onVesselsRef.current = onVessels
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const scopes = fleet.map(backendScopeFor)
    const action = fleetLayerAction(paused, scopes.length)
    if (action === 'skip') return
    if (action === 'clear') {
      map.removeVessels('vessels')
      return
    }

    const inputsSig =
      `${scopes.join(',')}|${species}|${showUncategorized}|${flags.join(',')}|` +
      `${String(orgId)}|${refreshKey}`
    const delay = fleetFetchDelayMs(prevSigRef.current, inputsSig)
    prevSigRef.current = inputsSig

    let alive = true

    const fetchAndDraw = () => {
      const bbox = map.getBBox()
      Promise.all(
        scopes.map(scope =>
          cabinetApi
            .fleetVessels({
              scope,
              speciesId: species,
              includeUncategorized: showUncategorized,
              bbox,
              // Фильтр флагов осмыслен только для чужого флота; своему и РФ его
              // не шлём, чтобы случайный выбор не гасил их слои.
              flags: scope === 'foreign' ? flags : undefined,
            })
            .then(fc => ({ scope, fc }))
            .catch(() => null),
        ),
      ).then(results => {
        if (!alive) return
        // Все запросы упали — чужого/устаревшего под своей подписью не рисуем.
        if (results.every(r => r === null)) {
          map.removeVessels('vessels')
          return
        }
        // Дедуп по mmsi: результаты в каноническом порядке (own→ru→foreign),
        // копия из 'own' перекрывает остальные — только в ней есть company_key.
        const byKey = new Map<string, GeoJSON.Feature>()
        for (const r of results) {
          if (!r) continue
          const ownScope = r.scope === 'own'
          for (const f of r.fc.features) {
            const mmsi = String((f.properties as { mmsi?: unknown })?.mmsi ?? '')
            const key = mmsi || `@${(f.geometry as GeoJSON.Point).coordinates.join(',')}`
            if (ownScope || !byKey.has(key)) byKey.set(key, f as GeoJSON.Feature)
          }
        }
        // Флаги кадра приходят только со scope=foreign (там их и выбирают).
        const foreign = results.find(r => r && r.scope === 'foreign')
        const counts = (foreign?.fc as { meta?: { flags?: FlagCount[] } } | undefined)?.meta?.flags
        if (counts) onFlagCountsRef.current?.(counts)

        const merged: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [...byKey.values()],
        }
        map.setVessels('vessels', merged, 0.9)
        // Держателям снимка — после укладки на карту: карточка обязана
        // догонять то, что человек уже видит, а не обгонять его.
        onVesselsRef.current?.(merged.features)
      })
    }

    // Клик тумблера/смена вида → delay=0 → рисуем немедленно (без 250 мс лага).
    // Пан/зум → delay=250 → гасим шторм moveend.
    let debounce: ReturnType<typeof setTimeout> | undefined
    if (delay === 0) fetchAndDraw()
    else debounce = setTimeout(fetchAndDraw, delay)
    const hourly = setInterval(fetchAndDraw, VESSELS_REFRESH_MS)
    return () => {
      alive = false
      if (debounce) clearTimeout(debounce)
      clearInterval(hourly)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleet, species, showUncategorized, flags, viewKey, orgId, refreshKey, paused])
}
